import { useState, useEffect } from "react";
import { collection, addDoc, doc, getDocs, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import Quill styles
import "./CreatePost.css"; // Optional for styling

function CreatePost() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(""); // Quill handles content as HTML
  const [imageUrl, setImageUrl] = useState("");
  const [author, setAuthor] = useState("");
  const [tags, setTags] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editPostId, setEditPostId] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    const postsSnapshot = await getDocs(collection(db, "posts"));
    const postsData = postsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setPosts(postsData);
  };

  const handleEdit = (postId) => {
    const postData = posts.find(post => post.id === postId);
    setTitle(postData.title);
    setContent(postData.content);
    setImageUrl(postData.imageUrl);
    setAuthor(postData.author);
    setTags(postData.tags.join(", "));
    setIsEditMode(true);
    setEditPostId(postId);
  };

  const handleDelete = (postId, postTitle) => {
    setConfirmationMessage(`Are you sure you want to delete the post titled "${postTitle}"?`);
    setShowConfirmation(true);
    // Set editPostId here if needed
    setEditPostId(postId);
  };

  const confirmDelete = async () => {
    await deleteDoc(doc(db, "posts", editPostId));
    fetchPosts();
    setShowConfirmation(false);
  };

  const cancelDelete = () => {
    setShowConfirmation(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const updatedContent = addIdsToHeadings(content);

      if (isEditMode && editPostId) {
        const postRef = doc(db, "posts", editPostId);
        await updateDoc(postRef, {
          title,
          content: updatedContent,
          imageUrl,
          author,
          tags: tags ? tags.split(",").map(tag => tag.trim()) : []
        });
        alert("Post Updated Successfully");
      } else {
        await addDoc(collection(db, "posts"), {
          title,
          content: updatedContent,
          imageUrl,
          author,
          tags: tags ? tags.split(",").map(tag => tag.trim()) : []
        });
        alert("New Post Created Successfully");
      }
      fetchPosts();
      navigate("/");
    } catch (error) {
      console.error("Error saving post:", error);
    }
  };

  // Utility function to add unique IDs to headings
  const addIdsToHeadings = (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3, h4, h5, h6');
    headings.forEach((heading, index) => {
      if (!heading.id) {
        heading.id = `heading-${index}`;
      }
    });
    return doc.body.innerHTML;
  };

  return (
    <div className="create-post">
      <h2>{isEditMode ? "Edit Post" : "Create New Post"}</h2>
      <form onSubmit={handleSubmit} className="create-post-form">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Title"
          required
        />
        <ReactQuill
          value={content}
          onChange={setContent}
          placeholder="Content"
          modules={{
            toolbar: [
              [{ 'header': '1'}, {'header': '2'}, { 'font': [] }],
              [{ 'list': 'ordered'}, { 'list': 'bullet' }],
              ['bold', 'italic', 'underline', 'strike', 'blockquote'],
              [{ 'script': 'sub'}, { 'script': 'super' }],
              [{ 'indent': '-1'}, { 'indent': '+1' }, { 'direction': 'rtl' }],
              [{ 'color': [] }, { 'background': [] }],
              [{ 'align': [] }],
              ['link', 'image', 'video'],
              ['clean']
            ],
          }}
          formats={[
            'header', 'font', 'size',
            'bold', 'italic', 'underline', 'strike', 'blockquote',
            'list', 'bullet', 'indent',
            'link', 'image', 'video', 'color', 'background', 'align'
          ]}
          theme="snow"
        />
        <input
          type="text"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          placeholder="Image URL"
        />
        <input
          type="text"
          value={author}
          onChange={(e) => setAuthor(e.target.value)}
          placeholder="Author"
          required
        />
        <input
          type="text"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="Tags (comma separated)"
        />
        <button type="submit">{isEditMode ? "Update Post" : "Create Post"}</button>
      </form>

      <div className="dashboard">
        <h2>All Posts</h2>
        <ul>
          {posts.map(post => (
            <li key={post.id}>
              <span>{post.title}</span>
              <button className="edit-btn" onClick={() => handleEdit(post.id)}>Edit</button>
              <button onClick={() => handleDelete(post.id, post.title)}>Delete</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Custom Confirmation Modal */}
      {showConfirmation && (
        <div className="custom-modal">
          <div className="modal-content">
            <p>{confirmationMessage}</p>
            <div className="button-container">
              <button onClick={confirmDelete}>Confirm</button>
              <button onClick={cancelDelete}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreatePost;
